import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getPendingUsers,
  getAllUsers,
  approveUser as approveUserApi,
  deleteUser as deleteUserApi,
  toggleUserAdminRole as toggleUserAdminRoleApi,
} from '@/services/supabaseUsers';
import { supabase } from '@/integrations/supabase/client';
import { normalizeUsers, getUsersArray, selectUserById } from '@/lib/utils';

/**
 * A custom hook for managing system administration tasks, such as fetching
 * pending and all users, and performing actions like approving, deleting,
 * and changing user roles.
 *
 * @returns An object with properties for pending users, all users, loading states,
 * error states, and functions to mutate user data.
 */
const useSystemAdmin = () => {
  const queryClient = useQueryClient();

  // Fetch pending users
  const {
    data: pendingUsers,
    isLoading: isLoadingPending,
    error: errorPending,
    refetch: refetchPendingUsers,
  } = useQuery({
    queryKey: ['pendingUsers'],
    queryFn: getPendingUsers,
    staleTime: 5 * 60 * 1000, // 5 minutes
    cacheTime: 10 * 60 * 1000, // 10 minutes
    refetchOnWindowFocus: true,
    retry: 2,
  });

  // Fetch all users
  const {
    data: allUsers,
    isLoading: isLoadingAll,
    error: errorAll,
    refetch: refetchAllUsers,
  } = useQuery({
    queryKey: ['allUsers'],
    queryFn: getAllUsers,
    staleTime: 5 * 60 * 1000, // 5 minutes
    cacheTime: 10 * 60 * 1000, // 10 minutes
    refetchOnWindowFocus: true,
    retry: 2,
  });

  // Approve user mutation
  const { mutate: approveUser } = useMutation({
    mutationFn: approveUserApi,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pendingUsers'] });
      queryClient.invalidateQueries({ queryKey: ['allUsers'] });
    },
  });

  // Delete user mutation
  const { mutate: deleteUser } = useMutation({
    mutationFn: async (userId: string) => {
      // Soft-delete: set is_employee to false
      const { error } = await supabase
        .from('profiles')
        .update({ is_employee: false })
        .eq('id', userId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pendingUsers'] });
      queryClient.invalidateQueries({ queryKey: ['allUsers'] });
    },
  });

  // Toggle admin role mutation
  const { mutate: toggleUserAdminRole } = useMutation({
    mutationFn: ({ userId, isAdmin }: { userId: string; isAdmin: boolean }) =>
      toggleUserAdminRoleApi(userId, isAdmin),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['allUsers'] });
    },
  });

  // Normalized user data
  const normalizedPendingUsers = pendingUsers ? normalizeUsers(pendingUsers) : {};
  const normalizedAllUsers = allUsers ? normalizeUsers(allUsers) : {};

  // Helper functions for normalized data access
  const getPendingUserById = (userId: string) => selectUserById(userId, normalizedPendingUsers);
  const getAllUserById = (userId: string) => selectUserById(userId, normalizedAllUsers);

  return {
    pendingUsers,
    allUsers,
    pendingUsersNormalized: normalizedPendingUsers,
    allUsersNormalized: normalizedAllUsers,
    isLoading: isLoadingPending || isLoadingAll,
    error: errorPending || errorAll,
    getPendingUserById,
    getAllUserById,
    getPendingUsersArray: () => getUsersArray(normalizedPendingUsers),
    getAllUsersArray: () => getUsersArray(normalizedAllUsers),
    approveUser,
    deleteUser,
    toggleUserAdminRole,
    refetchPendingUsers,
    refetchAllUsers,
  };
};

export default useSystemAdmin;
